import React, { useState, useRef, useEffect } from 'react';
import './QualityTooltip.css';

/**
 * QualityTooltip component - displays detailed quality information on hover
 * @param {Object} judgment - Quality judgment object with score, passed, dimensions, etc.
 * @param {ReactNode} children - The element to wrap (usually the QualityBadge)
 */
const QualityTooltip = ({ judgment, children }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef(null);
  const tooltipRef = useRef(null);

  // Handle positioning to avoid viewport overflow
  useEffect(() => {
    if (showTooltip && wrapperRef.current && tooltipRef.current) {
      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let top = wrapperRect.top - tooltipRect.height - 8; // Position above by default
      let left = wrapperRect.left + (wrapperRect.width / 2) - (tooltipRect.width / 2); // Center horizontally
      
      // Adjust if tooltip would overflow viewport
      if (top < 10) {
        // If too close to top, position below instead
        top = wrapperRect.bottom + 8;
      }
      if (left < 10) {
        left = 10;
      }
      if (left + tooltipRect.width > viewportWidth - 10) {
        left = viewportWidth - tooltipRect.width - 10;
      }
      if (top + tooltipRect.height > viewportHeight - 10) {
        top = viewportHeight - tooltipRect.height - 10;
      }
      
      setTooltipPosition({ top, left });
    }
  }, [showTooltip]);

  if (!judgment) {
    return children;
  }

  const score = judgment.score || 0;
  const passed = judgment.passed || false;
  const dimensions = judgment.quality_dimensions || {};
  const issues = judgment.issues || [];
  const strengths = judgment.strengths || [];
  const retryCount = judgment.retry_count || 0;

  return (
    <div
      ref={wrapperRef}
      className="quality-tooltip-wrapper"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {children}
      {showTooltip && (
        <div
          ref={tooltipRef}
          className="quality-tooltip"
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`
          }}
        >
          <div className="quality-tooltip-header">
            <h4>AI Quality Check</h4>
            <span className={`quality-status-badge ${passed ? 'passed' : 'failed'}`}>
              {passed ? '✓ Passed' : '✗ Failed'}
            </span>
          </div>
          
          <div className="quality-score-display">
            <span className="score-value">{score.toFixed(1)}</span>
            <span className="score-max">/10</span>
          </div>

          {Object.keys(dimensions).length > 0 && (
            <div className="quality-dimensions">
              <h5>Quality Dimensions</h5>
              <div className="dimensions-list">
                {Object.entries(dimensions).map(([dimension, dimScore]) => (
                  <div key={dimension} className="dimension-item">
                    <span className="dimension-name">{dimension.charAt(0).toUpperCase() + dimension.slice(1).replace('_', ' ')}</span>
                    <div className="dimension-bar-container">
                      <div 
                        className="dimension-bar" 
                        style={{ width: `${(dimScore / 10) * 100}%` }}
                      />
                    </div>
                    <span className="dimension-score">{dimScore.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {strengths.length > 0 && (
            <div className="quality-strengths">
              <h5>Strengths</h5>
              <ul>
                {strengths.map((strength, idx) => (
                  <li key={idx}>{strength}</li>
                ))}
              </ul>
            </div>
          )}

          {issues.length > 0 && (
            <div className="quality-issues">
              <h5>Issues</h5>
              <ul>
                {issues.map((issue, idx) => (
                  <li key={idx}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {retryCount > 0 && (
            <div className="quality-retry-info">
              <span className="retry-indicator">Retried {retryCount} time{retryCount > 1 ? 's' : ''}</span>
            </div>
          )}

          {judgment.reasoning && (
            <div className="quality-reasoning">
              <h5>Reasoning</h5>
              <p>{judgment.reasoning}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QualityTooltip;



